<template>
  <div>
    <component
      :is="embedded ? 'div' : 'v-dialog'"
      v-bind="dialogShellProps"
      @input="onDialogShellInput"
    >
      <v-card :flat="embedded" :class="{ 'embedded-grade-card': embedded }">
        <v-card-title v-if="!embedded" class="dialog-header">
          <v-icon class="mr-3" color="white" size="28">mdi-settings</v-icon>
          <span class="dialog-title">Bus Change Settings by Grade</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text" icon dark @click="closeGradesSetupDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Grades List View -->
        <v-card-text
          :class="embedded ? 'embedded-grade-body' : 'pa-3'"
          v-if="!editingGrade"
        >
          <v-layout row wrap class="mb-2" align-center>
            <v-flex xs12 sm3 class="pr-2">
              <v-btn-toggle
                v-model="gradesSetupSourceFilter"
                mandatory
                class="search-toggle"
              >
                <v-btn flat small value="school">
                  <v-icon small class="mr-1">mdi-school</v-icon>School
                </v-btn>
                <v-btn flat small value="daycamp">
                  <v-icon small class="mr-1">mdi-tent</v-icon>Day Camp
                </v-btn>
              </v-btn-toggle>
            </v-flex>
            <v-flex xs12 sm3 class="pr-2">
              <v-text-field
                v-model="gradesSetupSearch"
                placeholder="Search grades..."
                prepend-inner-icon="mdi-magnify"
                clearable
                solo
                flat
                dense
                hide-details
                class="compact-search grade-filter-field"
              ></v-text-field>
            </v-flex>
            <v-flex
              xs12
              sm3
              class="pr-2"
              v-if="isAdmin() && gradesSetupSourceFilter === 'school'"
            >
              <v-autocomplete
                v-model="gradesSetupBranchFilter"
                :items="branchList"
                placeholder="All Branches"
                solo
                flat
                dense
                hide-details
                clearable
                class="grade-filter-field"
              ></v-autocomplete>
            </v-flex>
            <v-spacer></v-spacer>
            <v-btn
              v-if="selectedGrades.length > 0"
              small
              color="success"
              outline
              @click="openBulkExceptionDialog"
              class="mr-2"
            >
              <v-icon small left>mdi-calendar-plus</v-icon>
              Add Exception ({{ selectedGrades.length }})
            </v-btn>
            <v-btn small color="success" dark @click="openAddGrade">
              <v-icon small left>mdi-plus</v-icon>
              Add Grade
            </v-btn>
          </v-layout>

          <div class="grades-list" v-if="!loadingGradesSetup">
            <div
              v-for="grade in filteredBusChangeGradesSearched"
              :key="grade.id"
              class="grade-row"
              @click="openEditGrade(grade)"
            >
              <div class="grade-select" @click.stop>
                <v-checkbox
                  v-model="selectedGrades"
                  :value="grade.id"
                  color="success"
                  hide-details
                  class="mt-0 pt-0"
                ></v-checkbox>
              </div>
              <div class="grade-info">
                <span class="grade-name">{{ grade.grade }}</span>
                <span class="grade-branch">
                  <v-icon x-small class="mr-1">{{
                    isDayCampGrade(grade)
                      ? "mdi-office-building"
                      : "mdi-source-branch"
                  }}</v-icon>
                  {{ isDayCampGrade(grade) ? grade.building : grade.branch }}
                </span>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      x-small
                      :color="grade.allowBusChange ? '#4caf50' : '#9e9e9e'"
                      class="ml-2"
                      v-on="on"
                    >
                      {{
                        grade.allowBusChange
                          ? "mdi-check-circle"
                          : "mdi-close-circle"
                      }}
                    </v-icon>
                  </template>
                  <span>{{
                    grade.allowBusChange
                      ? "Bus change allowed"
                      : "Bus change not allowed"
                  }}</span>
                </v-tooltip>
              </div>
              <div class="grade-actions" @click.stop>
                <v-btn class="my-0" icon x-small @click="openEditGrade(grade)">
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  class="my-0"
                  icon
                  x-small
                  @click="confirmDeleteGrade(grade)"
                >
                  <v-icon x-small color="error">mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
            <div
              v-if="filteredBusChangeGradesSearched.length === 0"
              class="no-grades"
            >
              <v-icon small color="grey" class="mr-1">mdi-school</v-icon>
              <span class="grey--text caption">No grades configured</span>
            </div>
          </div>
          <div v-else class="text-center pa-4">
            <v-progress-circular
              indeterminate
              color="#4caf50"
              size="24"
            ></v-progress-circular>
          </div>
        </v-card-text>

        <!-- Edit Grade View -->
        <v-card-text class="px-3 pt-1" v-else>
          <v-btn outline @click="backToGradesList" class="mb-3 ml-n2">
            <v-icon left>mdi-arrow-left</v-icon>
            Back to List
          </v-btn>

          <v-layout row wrap class="mb-3">
            <v-flex xs12 sm3 class="pr-2">
              <v-select
                v-model="currentGrade.source"
                :items="gradeSourceOptions"
                label="Source *"
                outlined
                dense
                :disabled="!!currentGrade.id"
                @change="onGradeSourceChange"
              ></v-select>
            </v-flex>
            <!-- School rules are keyed by Branch -->
            <v-flex xs12 sm3 class="pr-2" v-if="!isCurrentGradeDayCamp">
              <v-select
                v-model="currentGrade.branch"
                :items="branchList"
                label="Branch *"
                outlined
                dense
                :disabled="disableGradeSetupBranchSelection"
                @change="onGradeScopeChange"
              ></v-select>
            </v-flex>
            <!-- Day Camp rules are keyed by Building -->
            <v-flex xs12 sm3 class="pr-2" v-else>
              <v-select
                v-model="currentGrade.building"
                :items="buildingList"
                label="Building *"
                outlined
                dense
                :disabled="disableGradeSetupBuildingSelection"
                :loading="loadingBuildingList"
                @change="onGradeScopeChange"
              ></v-select>
            </v-flex>
            <v-flex xs12 sm3 class="pr-2">
              <v-select
                v-if="currentGrade.id"
                v-model="currentGrade.grade"
                :items="availableGrades"
                label="Grade *"
                outlined
                dense
                disabled
              ></v-select>
              <v-select
                v-else
                v-model="multipleGrades"
                :items="availableGrades"
                label="Grade(s) *"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                :disabled="!gradeScopeSelected"
                :loading="loadingAvailableGrades"
                hint="Select one or more grades"
                persistent-hint
              ></v-select>
            </v-flex>
            <v-flex xs12 sm3>
              <v-switch
                v-model="currentGrade.allowBusChange"
                label="Allow Bus Change"
                color="success"
                hide-details
                class="mt-1"
              ></v-switch>
            </v-flex>
            <v-flex xs12 sm6>
              <v-switch
                v-model="currentGrade.extendedSticker"
                label="Extended sticker report"
                color="success"
                hint="Turn this off if this grade should get the regular card on the first day of an extended bus change. The dates will show bigger, so you can see it covers more than one day."
                persistent-hint
                class="mt-1"
              ></v-switch>
            </v-flex>
            <v-flex xs12 sm4>
              <v-select
                v-model="currentGrade.busRabbiReportCopies"
                :items="busRabbiCopyOptions"
                label="Bus rabbi report copies"
                hint="How many copies of the bus rabbi report to print for this grade. 3 is the maximum."
                persistent-hint
                outlined
                dense
              ></v-select>
            </v-flex>
          </v-layout>

          <!-- Default Deadlines Section -->
          <div class="section-header mb-1">
            <v-icon small class="mr-1">mdi-clock-outline</v-icon>
            Default Deadlines (Sun - Fri)
          </div>
          <div class="caption grey--text text--darken-1 mb-2">
            These times repeat every week. Turn a day off to stop bus changes
            on that weekday every week. For one date, use Exception Deadlines.
          </div>

          <v-layout row wrap class="mb-3">
            <v-flex
              v-for="day in weekDays"
              :key="day.value"
              xs12
              sm4
              md2
              class="pa-1"
            >
              <v-card
                outlined
                class="pa-2 deadline-day-card"
                :class="{ 'deadline-day-closed': isDefaultDayClosed(day.value) }"
              >
                <div class="day-card-header mb-1">
                  <span class="caption font-weight-bold">{{ day.label }}</span>
                  <div class="day-switch">
                    <span
                      class="day-switch-label"
                      :class="
                        isDefaultDayClosed(day.value)
                          ? 'error--text'
                          : 'green--text'
                      "
                      >{{ isDefaultDayClosed(day.value) ? "Off" : "On" }}</span
                    >
                    <v-switch
                      :key="'sw-' + day.value + '-' + daySwitchKey"
                      :input-value="!isDefaultDayClosed(day.value)"
                      color="success"
                      hide-details
                      class="ma-0 pa-0"
                      :title="
                        isDefaultDayClosed(day.value)
                          ? 'Turn bus changes back on for ' + day.label
                          : 'Turn bus changes off every ' + day.label
                      "
                      @change="(on) => onDaySwitch(day, on)"
                    ></v-switch>
                  </div>
                </div>

                <div v-if="isDefaultDayClosed(day.value)">
                  <div class="closed-day-state">
                    <v-icon small color="error" class="mr-1"
                      >mdi-bus-alert</v-icon
                    >
                    <span class="error--text caption font-weight-medium"
                      >Bus changes off</span
                    >
                  </div>
                  <div class="closed-day-note">Every {{ day.label }}</div>
                </div>

                <div v-else>
                  <DxDateBox
                    type="time"
                    picker-type="list"
                    :interval="15"
                    :value="getDefaultDeadlineAsDate(day.value)"
                    @value-changed="
                      (e) => setDefaultDeadlineFromDate(day.value, e.value)
                    "
                    placeholder="Not set"
                    class="deadline-time-picker"
                  />
                  <div class="day-card-links">
                    <v-menu
                      offset-y
                      v-if="getDaysWithDeadlines(day.value).length > 0"
                    >
                      <template v-slot:activator="{ on }">
                        <span class="copy-from-link" v-on="on">
                          <v-icon x-small class="mr-1">mdi-content-copy</v-icon>
                          Copy from
                        </span>
                      </template>
                      <v-list dense>
                        <v-list-tile
                          v-for="sourceDay in getDaysWithDeadlines(day.value)"
                          :key="sourceDay.value"
                          @click="
                            copyDeadlineFromDay(sourceDay.value, day.value)
                          "
                        >
                          <v-list-tile-title>
                            {{ sourceDay.label }} ({{
                              formatTimeDisplay(
                                getDefaultDeadline(sourceDay.value)
                              )
                            }})
                          </v-list-tile-title>
                        </v-list-tile>
                      </v-list>
                    </v-menu>
                  </div>
                </div>
              </v-card>
            </v-flex>
          </v-layout>

          <!-- Exception Deadlines Section -->
          <div class="section-header mb-2 mt-4">
            <v-icon small class="mr-1">mdi-calendar</v-icon>
            Exception Deadlines
            <v-spacer></v-spacer>
            <v-btn small color="success" dark @click="addException">
              <v-icon x-small left>mdi-plus</v-icon>
              Add
            </v-btn>
          </div>

          <div class="exceptions-container" v-if="currentExceptions.length > 0">
            <div
              v-for="(exception, index) in currentExceptions.filter(
                (e) => !e._deleted
              )"
              :key="index"
              class="exception-chip"
            >
              <div class="exception-content">
                <v-icon small color="rgb(92, 184, 96)" class="mr-1"
                  >mdi-calendar</v-icon
                >
                <span class="exception-date">{{
                  formatExceptionDateShort(exception.exceptionDate)
                }}</span>
                <span class="exception-divider">•</span>
                <v-icon
                  x-small
                  class="mr-1"
                  :color="isNoBusChange(exception) ? 'error' : ''"
                  >{{
                    isNoBusChange(exception)
                      ? "mdi-bus-alert"
                      : "mdi-clock-outline"
                  }}</v-icon
                >
                <span
                  class="exception-time"
                  :class="{ 'error--text': isNoBusChange(exception) }"
                  >{{
                    isNoBusChange(exception)
                      ? "No bus changes"
                      : formatTimeDisplay(exception.deadline)
                  }}</span
                >
              </div>
              <div class="exception-actions">
                <v-btn
                  icon
                  x-small
                  @click="editException(exception)"
                  class="action-btn"
                >
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  @click="removeException(exception)"
                  class="action-btn delete-btn"
                >
                  <v-icon x-small color="error">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
          <div v-else class="no-exceptions">
            <v-icon small color="grey" class="mr-1">mdi-calendar-blank</v-icon>
            <span class="grey--text caption">No exceptions configured</span>
          </div>

          <div v-if="showUnsavedWarning" class="unsaved-warning mt-3">
            <v-icon x-small color="orange" class="mr-1">mdi-alert</v-icon>
            <span>You have unsaved changes. Click "Save Grade" to apply.</span>
          </div>
        </v-card-text>

        <v-divider v-if="editingGrade || !embedded"></v-divider>
        <v-card-actions class="pa-3" v-if="editingGrade">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="backToGradesList">
            Cancel
          </v-btn>
          <v-btn
            color="success"
            dark
            :loading="savingGradesSetup"
            @click="saveCurrentGrade"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Grade
          </v-btn>
        </v-card-actions>
        <v-card-actions class="pa-3" v-else-if="!embedded">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" dark @click="closeGradesSetupDialog">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </component>

    <!-- Close a weekday every week -->
    <v-dialog
      v-model="closeWeekdayDialog"
      max-width="460"
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="error">mdi-bus-alert</v-icon>
          Turn off bus changes every {{ closeWeekdayName }}?
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert type="warning" outline value="true" class="mb-3 pa-2">
            Parents and staff will not be able to place bus changes on
            <strong>every {{ closeWeekdayName }}</strong>, not just this week.
          </v-alert>
          <div>
            To turn off a single date, cancel and add it under
            <strong>Exception Deadlines</strong>.
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="cancelCloseWeekday">Cancel</v-btn>
          <v-btn color="error" dark @click="confirmCloseWeekday">
            Turn off every {{ closeWeekdayName }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add/Edit Exception Dialog -->
    <v-dialog v-model="exceptionDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ editingException ? "Edit Exception" : "Add Exception" }}
        </v-card-title>
        <v-card-text>
          <v-menu
            v-model="exceptionDateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="newException.exceptionDate"
                label="Date *"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="newException.exceptionDate"
              color="rgb(92, 184, 96)"
              @input="exceptionDateMenu = false"
            ></v-date-picker>
          </v-menu>
          <v-checkbox
            v-model="newException.noBusChange"
            label="No bus changes this day"
            color="error"
            hide-details
            class="mt-0 mb-2"
          ></v-checkbox>
          <div v-if="!newException.noBusChange" class="mb-3">
            <div class="caption mb-1">Deadline Time *</div>
            <DxDateBox
              type="time"
              picker-type="list"
              :interval="15"
              :value="getExceptionDeadlineAsDate()"
              @value-changed="setExceptionDeadlineFromDate"
              placeholder="Select time"
              class="deadline-time-picker"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="exceptionDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="success" dark @click="saveException">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Grade Confirmation -->
    <v-dialog v-model="deleteGradeDialog" max-width="400">
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left dark>mdi-alert-circle</v-icon>
          Delete Grade
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" outline value="true" class="mb-0 pa-2">
            Are you sure you want to delete this grade? This will also delete
            all associated deadlines.
          </v-alert>
          <div class="mt-3 subheading-1" v-if="gradeToDelete">
            <span class="font-weight-medium">Grade:</span>
            {{ gradeToDelete.grade }} ({{
              isDayCampGrade(gradeToDelete)
                ? gradeToDelete.building
                : gradeToDelete.branch
            }})
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="deleteGradeDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="error"
            dark
            :loading="deletingGrade"
            @click="deleteGrade"
            >Delete</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Add Exception Dialog -->
    <v-dialog v-model="bulkExceptionDialog" max-width="450">
      <v-card>
        <v-card-title class="headline primary white--text">
          <v-icon left dark>mdi-calendar-plus</v-icon>
          Add Exception to Selected Grades
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-3">
            <div class="caption grey--text mb-2">
              Selected Grades ({{ selectedGrades.length }})
            </div>
            <div class="selected-grades-list">
              <div
                v-for="grade in getSelectedGradeDetails()"
                :key="grade.id"
                class="selected-grade-item"
              >
                <span class="grade-text">
                  <strong>{{ grade.grade }}</strong>
                  <span class="grey--text ml-2"
                    >({{
                      isDayCampGrade(grade) ? grade.building : grade.branch
                    }})</span
                  >
                </span>
              </div>
            </div>
          </div>
          <v-menu
            v-model="bulkExceptionDateMenu"
            :close-on-content-click="false"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="bulkException.exceptionDate"
                label="Date *"
                prepend-inner-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="bulkException.exceptionDate"
              color="rgb(92, 184, 96)"
              @input="bulkExceptionDateMenu = false"
            ></v-date-picker>
          </v-menu>
          <v-checkbox
            v-model="bulkException.noBusChange"
            label="No bus changes this day"
            color="error"
            hide-details
            class="mt-0 mb-2"
          ></v-checkbox>
          <div v-if="!bulkException.noBusChange" class="mb-3">
            <div class="caption mb-1">Deadline Time *</div>
            <DxDateBox
              type="time"
              picker-type="list"
              :interval="15"
              :value="getBulkExceptionDeadlineAsDate()"
              @value-changed="setBulkExceptionDeadlineFromDate"
              placeholder="Select time"
              class="deadline-time-picker"
            />
          </div>
        </v-card-text>
        <v-card-actions class="pb-3">
          <v-spacer></v-spacer>
          <v-btn color="grey" dark @click="bulkExceptionDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            dark
            :loading="savingBulkException"
            @click="saveBulkException"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Exception
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DxDateBox from "devextreme-vue/date-box";
import { isNoBusChangeDeadline } from "../busChangeDeadline";

export default {
  name: "BusChangeGradesSetupDialog",
  components: {
    DxDateBox,
  },
  props: {
    embedded: { type: Boolean, default: false },
  },
  data: () => ({
    gradesSetupDialog: false,
    gradesSetupSearch: "",
    gradesSetupBranchFilter: null,
    gradesSetupSourceFilter: "school",
    gradeSourceOptions: [
      { text: "School", value: "school" },
      { text: "Day Camp", value: "daycamp" },
    ],
    branchList: [],
    buildingList: [],
    loadingBuildingList: false,
    userDayCampBuildings: [],
    loadingGradesSetup: false,
    savingGradesSetup: false,
    busChangeGrades: [],
    editingGrade: false,
    currentGrade: {
      id: null,
      grade: "",
      branch: "",
      building: "",
      source: "school",
      allowBusChange: true,
      extendedSticker: true,
      busRabbiReportCopies: 2,
    },
    busRabbiCopyOptions: [
      { text: "1", value: 1 },
      { text: "2", value: 2 },
      { text: "3", value: 3 },
    ],
    currentDeadlines: [],
    currentExceptions: [],
    selectedGrades: [],
    bulkExceptionDialog: false,
    bulkExceptionDateMenu: false,
    savingBulkException: false,
    bulkException: { exceptionDate: "", deadline: "", noBusChange: false },
    weekDays: [
      { value: 0, label: "Sunday" },
      { value: 1, label: "Monday" },
      { value: 2, label: "Tuesday" },
      { value: 3, label: "Wednesday" },
      { value: 4, label: "Thursday" },
      { value: 5, label: "Friday" },
    ],
    closeWeekdayDialog: false,
    closeWeekdayTarget: null,
    daySwitchKey: 0,
    exceptionDialog: false,
    exceptionDateMenu: false,
    editingException: null,
    newException: { exceptionDate: "", deadline: "", noBusChange: false },
    showUnsavedWarning: false,
    deleteGradeDialog: false,
    deletingGrade: false,
    gradeToDelete: null,
    availableGrades: [],
    loadingAvailableGrades: false,
    multipleGrades: [],
  }),

  computed: {
    dialogShellProps() {
      if (this.embedded) return {};
      return {
        value: this.gradesSetupDialog,
        maxWidth: "1000",
        persistent: true,
      };
    },
    userBranch() {
      const branch = this.$store.state.authentication.branch;
      if (branch && !branch.includes("null")) {
        return branch;
      }
      return null;
    },
    permittedBranches() {
      if (!this.userBranch) return [];
      return this.userBranch
        .split(",")
        .map((b) => b.trim())
        .filter(Boolean);
    },
    disableGradeSetupBranchSelection() {
      if (this.currentGrade.id) return true;
      if (this.isAdmin()) return false;
      return this.permittedBranches.length <= 1;
    },
    disableGradeSetupBuildingSelection() {
      if (this.currentGrade.id) return true;
      if (this.isAdmin()) return false;
      return (
        this.hasUserDayCampBuildings() && this.userDayCampBuildings.length <= 1
      );
    },
    isCurrentGradeDayCamp() {
      return this.currentGrade.source === "daycamp";
    },
    closeWeekdayName() {
      return (
        (this.closeWeekdayTarget && this.closeWeekdayTarget.label) || "this day"
      );
    },
    gradeScopeSelected() {
      // The "scope" is the field a grade rule is keyed by: Branch (school) or Building (day camp).
      return this.isCurrentGradeDayCamp
        ? !!this.currentGrade.building
        : !!this.currentGrade.branch;
    },
    filteredBusChangeGrades() {
      const wantDayCamp = this.gradesSetupSourceFilter === "daycamp";
      let grades = this.busChangeGrades.filter(
        (g) => this.isDayCampGrade(g) === wantDayCamp
      );

      if (wantDayCamp) {
        // Day Camp rules are keyed by Building — restrict non-admins with DayCampBuilding attributes.
        if (this.hasUserDayCampBuildings()) {
          grades = grades.filter((g) =>
            this.userDayCampBuildings.includes(
              g.building ? g.building.trim() : ""
            )
          );
        }
      } else if (!this.isAdmin() && this.userBranch) {
        grades = grades.filter((g) =>
          this.permittedBranches.includes(g.branch)
        );
      } else if (this.gradesSetupBranchFilter) {
        grades = grades.filter(
          (g) => g.branch === this.gradesSetupBranchFilter
        );
      }
      return grades;
    },
    filteredBusChangeGradesSearched() {
      let grades = this.filteredBusChangeGrades;
      if (this.gradesSetupSearch) {
        const search = this.gradesSetupSearch.toLowerCase();
        grades = grades.filter(
          (g) =>
            (g.grade && g.grade.toLowerCase().includes(search)) ||
            (g.branch && g.branch.toLowerCase().includes(search)) ||
            (g.building && g.building.toLowerCase().includes(search))
        );
      }
      return grades;
    },
  },

  mounted() {
    if (this.embedded) this.open();
  },

  methods: {
    onDialogShellInput(value) {
      if (this.embedded) return;
      this.gradesSetupDialog = value;
    },

    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];
      if (!isAdmin || isAdmin == "false") return false;
      return true;
    },

    hasUserDayCampBuildings() {
      return !this.isAdmin() && this.userDayCampBuildings.length > 0;
    },

    isDayCampGrade(grade) {
      return (
        !!grade && (grade.source || "").trim().toLowerCase() === "day camp"
      );
    },

    async open() {
      this.gradesSetupDialog = true;
      this.editingGrade = false;
      this.selectedGrades = [];
      this.loadingGradesSetup = true;
      await Promise.all([
        this.loadBusChangeGrades(),
        this.loadBranches(),
        this.loadUserDayCampBuildings(),
      ]);
      this.loadingGradesSetup = false;
    },

    closeGradesSetupDialog() {
      this.gradesSetupDialog = false;
      this.editingGrade = false;
      this.selectedGrades = [];
      this.resetCurrentGrade();
    },

    async loadUserDayCampBuildings() {
      const username = this.$store.state.authentication.user;
      if (!username) {
        this.userDayCampBuildings = [];
        return;
      }
      try {
        const attributes = await this.$http.get(
          "api/users/GetUserAttributesByUsername?username=" +
            encodeURIComponent(username)
        );
        this.userDayCampBuildings = (attributes || [])
          .filter((a) => a && a.name && a.name.trim() === "DayCampBuilding")
          .map((a) => (a.value ? a.value.trim() : ""))
          .filter(Boolean);
      } catch (e) {
        this.userDayCampBuildings = [];
      }
    },

    async loadBranches() {
      try {
        const branchesResponse = await this.$http.get(
          "api/students/getBranchList"
        );
        let branches = Array.isArray(branchesResponse) ? branchesResponse : [];

        // Non-admin users should only see their permitted branches.
        if (!this.isAdmin() && this.permittedBranches.length) {
          const filteredBranches = branches.filter((b) =>
            this.permittedBranches.includes(b)
          );
          branches =
            filteredBranches.length > 0
              ? filteredBranches
              : this.permittedBranches;
        }

        this.branchList = branches;
      } catch (e) {
        this.branchList = [];
      }
    },

    async loadBusChangeGrades() {
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeGrades"
        );
        this.busChangeGrades = Array.isArray(response) ? response : [];
      } catch (e) {
        this.busChangeGrades = [];
      }
    },

    async reloadGradesAndNotify() {
      await this.loadBusChangeGrades();
      this.$emit("changed", this.busChangeGrades);
    },

    async openAddGrade() {
      this.resetCurrentGrade();
      // Default the new grade's source to whichever tab is currently active.
      this.currentGrade.source = this.gradesSetupSourceFilter;

      if (this.isCurrentGradeDayCamp) {
        await this.loadBuildingList();
        if (
          this.hasUserDayCampBuildings() &&
          this.userDayCampBuildings.length === 1
        ) {
          this.currentGrade.building = this.userDayCampBuildings[0];
          await this.loadAvailableGrades();
        }
      } else if (!this.isAdmin() && this.permittedBranches.length === 1) {
        // Only auto-select when there is a single permitted branch.
        this.currentGrade.branch = this.permittedBranches[0];
        await this.loadAvailableGrades();
      }
      this.editingGrade = true;
    },

    async onGradeSourceChange() {
      // Switching source resets the scope (branch/building) and grade selections.
      this.currentGrade.grade = "";
      this.currentGrade.branch = "";
      this.currentGrade.building = "";
      this.multipleGrades = [];
      this.availableGrades = [];

      if (this.isCurrentGradeDayCamp) {
        await this.loadBuildingList();
        if (
          this.hasUserDayCampBuildings() &&
          this.userDayCampBuildings.length === 1
        ) {
          this.currentGrade.building = this.userDayCampBuildings[0];
          await this.loadAvailableGrades();
        }
      } else if (!this.isAdmin() && this.permittedBranches.length === 1) {
        this.currentGrade.branch = this.permittedBranches[0];
        await this.loadAvailableGrades();
      }
    },

    busRabbiCopiesLabel(grade) {
      const n = parseInt(grade && grade.busRabbiReportCopies, 10);
      if (n === 1 || n === 2 || n === 3) return n;
      return 2;
    },

    async onGradeScopeChange() {
      // The scope (branch or building) drives the list of available grades.
      this.currentGrade.grade = "";
      this.multipleGrades = [];
      if (this.gradeScopeSelected) {
        await this.loadAvailableGrades();
      } else {
        this.availableGrades = [];
      }
    },

    async loadAvailableGrades() {
      this.loadingAvailableGrades = true;
      try {
        const url = this.isCurrentGradeDayCamp
          ? "api/students/getDayCampGradeList?building=" +
            encodeURIComponent(this.currentGrade.building || "")
          : "api/students/getGradeList?branch=" +
            encodeURIComponent(this.currentGrade.branch || "");
        const response = await this.$http.get(url);
        this.availableGrades = Array.isArray(response) ? response : [];
      } catch (e) {
        this.availableGrades = [];
      } finally {
        this.loadingAvailableGrades = false;
      }
    },

    async loadBuildingList() {
      this.loadingBuildingList = true;
      try {
        const response = await this.$http.get(
          "api/students/getDayCampBuildingList"
        );
        let buildings = Array.isArray(response) ? response : [];

        if (this.hasUserDayCampBuildings()) {
          const filtered = buildings.filter((b) =>
            this.userDayCampBuildings.includes(b ? String(b).trim() : "")
          );
          buildings =
            filtered.length > 0 ? filtered : this.userDayCampBuildings.slice();
        }

        this.buildingList = buildings;
      } catch (e) {
        this.buildingList = this.hasUserDayCampBuildings()
          ? this.userDayCampBuildings.slice()
          : [];
      } finally {
        this.loadingBuildingList = false;
      }
    },

    async openEditGrade(grade) {
      const isDayCamp = this.isDayCampGrade(grade);
      this.currentGrade = {
        id: grade.id,
        grade: grade.grade,
        branch: grade.branch || "",
        building: grade.building || "",
        source: isDayCamp ? "daycamp" : "school",
        allowBusChange: grade.allowBusChange,
        extendedSticker: grade.extendedSticker !== false,
        busRabbiReportCopies: this.busRabbiCopiesLabel(grade),
      };
      this.editingGrade = true;

      const tasks = [
        this.loadAvailableGrades(),
        this.loadGradeDeadlines(grade.id),
      ];
      if (isDayCamp) {
        tasks.push(this.loadBuildingList());
      }
      await Promise.all(tasks);
    },

    async loadGradeDeadlines(gradeId) {
      try {
        const response = await this.$http.get(
          "api/busChange/getBusChangeDeadlines?gradeId=" + gradeId
        );
        const deadlines = Array.isArray(response) ? response : [];
        this.currentDeadlines = deadlines.filter((d) => !d.exceptionDate);
        this.currentExceptions = deadlines.filter((d) => d.exceptionDate);
      } catch (e) {
        this.currentDeadlines = [];
        this.currentExceptions = [];
      }
    },

    resetCurrentGrade() {
      this.currentGrade = {
        id: null,
        grade: "",
        branch: "",
        building: "",
        source: this.gradesSetupSourceFilter || "school",
        allowBusChange: true,
        extendedSticker: true,
        busRabbiReportCopies: 2,
      };
      this.currentDeadlines = [];
      this.currentExceptions = [];
      this.availableGrades = [];
      this.multipleGrades = [];
      this.showUnsavedWarning = false;
      this.closeWeekdayDialog = false;
      this.closeWeekdayTarget = null;
    },

    backToGradesList() {
      this.editingGrade = false;
      this.resetCurrentGrade();
    },

    formatDateForPicker(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatTimeDisplay(timeStr) {
      if (!timeStr) return "";
      const parts = timeStr.split(":");
      if (parts.length < 2) return "";
      let hours = parseInt(parts[0]);
      const minutes = parts[1];
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return hours + ":" + minutes + " " + ampm;
    },

    parseTimeForPicker(timeStr) {
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      return parts[0].padStart(2, "0") + ":" + parts[1];
    },

    getDefaultDeadlineRecord(dayOfWeek) {
      return this.currentDeadlines.find(
        (d) =>
          !d._deleted &&
          !d.exceptionDate &&
          Number(d.dayOfWeek) === Number(dayOfWeek)
      );
    },

    isDefaultDayClosed(dayOfWeek) {
      return isNoBusChangeDeadline(this.getDefaultDeadlineRecord(dayOfWeek));
    },

    getDefaultDeadline(dayOfWeek) {
      const deadline = this.getDefaultDeadlineRecord(dayOfWeek);
      if (!deadline || isNoBusChangeDeadline(deadline)) return null;
      return deadline.deadline;
    },

    setDefaultDeadline(dayOfWeek, timeValue) {
      const existing = this.getDefaultDeadlineRecord(dayOfWeek);
      const deadline =
        timeValue && timeValue.length === 5 ? timeValue + ":00" : timeValue;
      if (existing) {
        existing.deadline = deadline;
        existing.type = "Default";
        existing.exceptionDate = null;
        existing._changed = true;
      } else {
        this.currentDeadlines.push({
          id: null,
          gradeID: this.currentGrade.id,
          dayOfWeek: dayOfWeek,
          type: "Default",
          exceptionDate: null,
          deadline: deadline,
          _isNew: true,
        });
      }
      this.showUnsavedWarning = true;
    },

    onDaySwitch(day, on) {
      if (on) {
        this.reopenDefaultDay(day);
      } else {
        this.closeWeekdayTarget = day;
        this.closeWeekdayDialog = true;
      }
    },

    cancelCloseWeekday() {
      this.closeWeekdayDialog = false;
      this.closeWeekdayTarget = null;
      // Switch already flipped itself off; re-render it from the real state.
      this.daySwitchKey++;
    },

    reopenDefaultDay(day) {
      const existing = this.getDefaultDeadlineRecord(day.value);
      if (!isNoBusChangeDeadline(existing)) return;

      if (existing._prevDeadline) {
        existing.type = "Default";
        existing.deadline = existing._prevDeadline;
        existing._prevDeadline = null;
        existing._changed = true;
      } else if (existing.id) {
        existing._deleted = true;
      } else {
        this.currentDeadlines.splice(
          this.currentDeadlines.indexOf(existing),
          1
        );
      }
      this.showUnsavedWarning = true;
    },

    confirmCloseWeekday() {
      const day = this.closeWeekdayTarget;
      this.closeWeekdayDialog = false;
      this.closeWeekdayTarget = null;
      if (!day) return;

      const existing = this.getDefaultDeadlineRecord(day.value);
      if (existing) {
        if (!isNoBusChangeDeadline(existing)) {
          existing._prevDeadline = existing.deadline;
        }
        existing.type = "NoBusChange";
        existing.exceptionDate = null;
        existing.deadline = "00:00:00";
        existing._changed = true;
      } else {
        this.currentDeadlines.push({
          id: null,
          gradeID: this.currentGrade.id,
          dayOfWeek: day.value,
          type: "NoBusChange",
          exceptionDate: null,
          deadline: "00:00:00",
          _isNew: true,
        });
      }
      this.showUnsavedWarning = true;
    },

    getDefaultDeadlineAsDate(dayOfWeek) {
      const timeStr = this.getDefaultDeadline(dayOfWeek);
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      const date = new Date();
      date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
      return date;
    },

    setDefaultDeadlineFromDate(dayOfWeek, dateValue) {
      if (!dateValue) return;
      const hours = String(dateValue.getHours()).padStart(2, "0");
      const minutes = String(dateValue.getMinutes()).padStart(2, "0");
      const next = `${hours}:${minutes}`;
      if (this.parseTimeForPicker(this.getDefaultDeadline(dayOfWeek)) === next) {
        return;
      }
      this.setDefaultDeadline(dayOfWeek, next);
    },

    getExceptionDeadlineAsDate() {
      const timeStr = this.newException.deadline;
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      const date = new Date();
      date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
      return date;
    },

    setExceptionDeadlineFromDate(e) {
      if (!e.value) return;
      const hours = String(e.value.getHours()).padStart(2, "0");
      const minutes = String(e.value.getMinutes()).padStart(2, "0");
      this.newException.deadline = `${hours}:${minutes}:00`;
    },

    getDaysWithDeadlines(excludeDay) {
      return this.weekDays.filter(
        (d) => d.value !== excludeDay && this.getDefaultDeadline(d.value)
      );
    },

    copyDeadlineFromDay(fromDay, toDay) {
      const sourceDeadline = this.getDefaultDeadline(fromDay);
      if (sourceDeadline) {
        const timeValue = this.parseTimeForPicker(sourceDeadline);
        this.setDefaultDeadline(toDay, timeValue);
      }
    },

    isNoBusChange(exception) {
      return isNoBusChangeDeadline(exception);
    },

    addException() {
      this.editingException = null;
      this.newException = {
        exceptionDate: "",
        deadline: "",
        noBusChange: false,
      };
      this.exceptionDialog = true;
    },

    editException(exception) {
      this.editingException = exception;
      this.newException = {
        exceptionDate: this.formatDateForPicker(exception.exceptionDate),
        deadline: exception.deadline,
        noBusChange: isNoBusChangeDeadline(exception),
      };
      this.exceptionDialog = true;
    },

    saveException() {
      if (
        !this.newException.exceptionDate ||
        (!this.newException.noBusChange && !this.newException.deadline)
      ) {
        this.$notify({
          text: this.newException.noBusChange
            ? "Please pick a date"
            : "Please fill in date and time",
          group: "notification",
          type: "error",
        });
        return;
      }

      const type = this.newException.noBusChange ? "NoBusChange" : "Exception";
      const deadline = this.newException.noBusChange
        ? "00:00:00"
        : this.newException.deadline;

      if (this.editingException) {
        this.editingException.exceptionDate = this.newException.exceptionDate;
        this.editingException.deadline = deadline;
        this.editingException.type = type;
        this.editingException._changed = true;
      } else {
        this.currentExceptions.push({
          id: null,
          gradeID: this.currentGrade.id,
          dayOfWeek: new Date(this.newException.exceptionDate).getDay(),
          type: type,
          exceptionDate: this.newException.exceptionDate,
          deadline: deadline,
          _isNew: true,
        });
      }
      this.showUnsavedWarning = true;
      this.exceptionDialog = false;
    },

    removeException(exception) {
      if (exception.id) {
        exception._deleted = true;
      } else {
        const index = this.currentExceptions.indexOf(exception);
        if (index > -1) {
          this.currentExceptions.splice(index, 1);
        }
      }

      this.showUnsavedWarning = true;
      this.$forceUpdate();
    },

    formatExceptionDateShort(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    },

    confirmDeleteGrade(grade) {
      this.gradeToDelete = grade;
      this.deleteGradeDialog = true;
    },

    async deleteGrade() {
      this.deletingGrade = true;
      try {
        await this.$http.post(
          "api/busChange/deleteBusChangeGrade?id=" + this.gradeToDelete.id
        );
        this.$notify({
          text: "Grade deleted successfully",
          group: "notification",
          type: "success",
        });
        this.deleteGradeDialog = false;
        await this.reloadGradesAndNotify();
      } catch (e) {
        this.$notify({
          text: e.message || "Failed to delete grade",
          group: "notification",
          type: "error",
        });
      } finally {
        this.deletingGrade = false;
      }
    },

    async saveCurrentGrade() {
      // Validation: day camp rules require a Building, school rules require a Branch.
      if (this.isCurrentGradeDayCamp && !this.currentGrade.building) {
        this.$notify({
          text: "Please select a building",
          group: "notification",
          type: "error",
        });
        return;
      }
      if (!this.isCurrentGradeDayCamp && !this.currentGrade.branch) {
        this.$notify({
          text: "Please select a branch",
          group: "notification",
          type: "error",
        });
        return;
      }

      const isNewGrade = !this.currentGrade.id;
      if (isNewGrade && this.multipleGrades.length === 0) {
        this.$notify({
          text: "Please select at least one grade",
          group: "notification",
          type: "error",
        });
        return;
      }

      if (!isNewGrade && !this.currentGrade.grade) {
        this.$notify({
          text: "Please select a grade",
          group: "notification",
          type: "error",
        });
        return;
      }

      this.savingGradesSetup = true;

      const gradesToSave = isNewGrade
        ? this.multipleGrades
        : [this.currentGrade.grade];

      const successGrades = [];
      const failedGrades = [];

      for (const gradeName of gradesToSave) {
        try {
          const gradeResponse = await this.$http.post(
            "api/busChange/saveBusChangeGrade",
            {
              id: isNewGrade ? 0 : this.currentGrade.id,
              grade: gradeName,
              source: this.isCurrentGradeDayCamp ? "Day Camp" : "School",
              branch: this.isCurrentGradeDayCamp
                ? null
                : this.currentGrade.branch,
              building: this.isCurrentGradeDayCamp
                ? this.currentGrade.building
                : null,
              allowBusChange: this.currentGrade.allowBusChange,
              extendedSticker: this.currentGrade.extendedSticker !== false,
              busRabbiReportCopies: this.busRabbiCopiesLabel(
                this.currentGrade
              ),
              createdBy: this.$store.state.authentication.user,
            }
          );

          const gradeId = gradeResponse.id || this.currentGrade.id;

          const allDeadlines = [
            ...this.currentDeadlines,
            ...this.currentExceptions,
          ];
          for (const deadline of allDeadlines) {
            if (deadline._deleted && deadline.id) {
              await this.$http.post(
                "api/busChange/deleteBusChangeDeadline?id=" + deadline.id
              );
            } else if (deadline._isNew || deadline._changed) {
              await this.$http.post("api/busChange/saveBusChangeDeadline", {
                id: isNewGrade ? 0 : deadline.id || 0,
                gradeID: gradeId,
                dayOfWeek: deadline.dayOfWeek,
                type: deadline.type,
                exceptionDate: deadline.exceptionDate,
                deadline: deadline.deadline,
                createdBy: this.$store.state.authentication.user,
              });
            }
          }

          successGrades.push(gradeName);
        } catch (e) {
          failedGrades.push(gradeName);
        }
      }

      if (gradesToSave.length === 1) {
        this.$notify({
          text:
            successGrades.length === 1
              ? "Grade saved successfully"
              : "Failed to save grade",
          group: "notification",
          type: successGrades.length === 1 ? "success" : "error",
        });
      } else if (failedGrades.length === 0) {
        this.$notify({
          text: `All ${successGrades.length} grades saved successfully`,
          group: "notification",
          type: "success",
        });
      } else if (successGrades.length === 0) {
        this.$notify({
          text: `Failed to save all ${failedGrades.length} grades`,
          group: "notification",
          type: "error",
        });
      } else {
        this.$notify({
          text: `Saved: ${successGrades.join(", ")}`,
          group: "notification",
          type: "success",
        });
        this.$notify({
          text: `Failed: ${failedGrades.join(", ")}`,
          group: "notification",
          type: "error",
        });
      }

      await this.reloadGradesAndNotify();

      if (successGrades.length > 0 || gradesToSave.length === 1) {
        this.backToGradesList();
      }

      this.savingGradesSetup = false;
    },

    openBulkExceptionDialog() {
      if (this.selectedGrades.length === 0) {
        this.$notify({
          text: "Please select at least one grade",
          group: "notification",
          type: "error",
        });
        return;
      }
      this.bulkException = {
        exceptionDate: "",
        deadline: "",
        noBusChange: false,
      };
      this.bulkExceptionDialog = true;
    },

    getSelectedGradeDetails() {
      return this.busChangeGrades.filter((g) =>
        this.selectedGrades.includes(g.id)
      );
    },

    getBulkExceptionDeadlineAsDate() {
      const timeStr = this.bulkException.deadline;
      if (!timeStr) return null;
      const parts = timeStr.split(":");
      if (parts.length < 2) return null;
      const date = new Date();
      date.setHours(parseInt(parts[0]), parseInt(parts[1]), 0, 0);
      return date;
    },

    setBulkExceptionDeadlineFromDate(e) {
      if (!e.value) return;
      const hours = String(e.value.getHours()).padStart(2, "0");
      const minutes = String(e.value.getMinutes()).padStart(2, "0");
      this.bulkException.deadline = `${hours}:${minutes}:00`;
    },

    async saveBulkException() {
      if (
        !this.bulkException.exceptionDate ||
        (!this.bulkException.noBusChange && !this.bulkException.deadline)
      ) {
        this.$notify({
          text: this.bulkException.noBusChange
            ? "Please pick a date"
            : "Please fill in date and time",
          group: "notification",
          type: "error",
        });
        return;
      }

      this.savingBulkException = true;
      try {
        await this.$http.post("api/busChange/BulkAddExceptions", {
          gradeIDs: this.selectedGrades,
          exceptionDate: this.bulkException.exceptionDate,
          deadline: this.bulkException.noBusChange
            ? "00:00:00"
            : this.bulkException.deadline,
          noBusChange: !!this.bulkException.noBusChange,
          createdBy: this.$store.state.authentication.user,
        });

        this.$notify({
          text: `Exception added to ${this.selectedGrades.length} grade(s) successfully`,
          group: "notification",
          type: "success",
        });

        this.bulkExceptionDialog = false;
        this.selectedGrades = [];
        await this.reloadGradesAndNotify();
      } catch (e) {
        // error toast comes from the http plugin
      } finally {
        this.savingBulkException = false;
      }
    },
  },
};
</script>

<style scoped>
.embedded-grade-card .grades-list {
  max-height: none;
}

.embedded-grade-body {
  padding: 8px 12px 12px !important;
}

.dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.section-header {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: rgb(92, 184, 96);
}

.exceptions-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.exception-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%);
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 6px 8px 6px 12px;
  font-size: 13px;
  transition: all 0.2s ease;
}

.exception-chip:hover {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-color: rgb(92, 184, 96);
}

.exception-content {
  display: flex;
  align-items: center;
}

.exception-date {
  font-weight: 500;
  color: #424242;
}

.exception-divider {
  margin: 0 6px;
  color: #bdbdbd;
}

.exception-time {
  color: #616161;
}

.exception-actions {
  display: flex;
  align-items: center;
  margin-left: 4px;
}

.exception-actions .action-btn {
  width: 22px;
  height: 22px;
  margin-left: 2px;
}

.exception-actions .action-btn:hover {
  background: rgba(0, 0, 0, 0.08);
}

.no-exceptions {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px dashed #e0e0e0;
  border-radius: 8px;
}

.copy-from-link {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: #4caf50;
  cursor: pointer;
  margin-top: 4px;
  transition: color 0.2s ease;
}

.copy-from-link:hover {
  color: #388e3c;
  text-decoration: underline;
}

.copy-from-link .v-icon {
  color: #4caf50 !important;
  font-size: 12px !important;
}

.copy-from-link:hover .v-icon {
  color: #388e3c !important;
}

.grades-list {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  max-height: 300px;
  overflow-y: auto;
}

.grade-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.grade-row:last-child {
  border-bottom: none;
}

.grade-row:hover {
  background-color: #f5f5f5;
}

.grade-select {
  display: flex;
  align-items: center;
  margin-right: 8px;
}

.grade-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.grade-name {
  font-weight: 600;
  color: #424242;
  min-width: 80px;
}

.grade-branch {
  color: #757575;
  font-size: 13px;
  margin-left: 16px;
}

.grade-actions {
  display: flex;
  align-items: center;
  opacity: 0.6;
  transition: opacity 0.15s ease;
}

.grade-row:hover .grade-actions {
  opacity: 1;
}

.no-grades {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #fafafa;
}

.compact-search {
  border-radius: 6px;
}

.grade-filter-field.v-text-field {
  padding-top: 0 !important;
  margin-top: 0 !important;
}

.grade-filter-field >>> .v-input__control {
  min-height: 28px !important;
}

.grade-filter-field >>> .v-input__slot {
  min-height: 28px !important;
  align-items: flex-end !important;
  padding: 0 4px 2px !important;
  border-bottom: 1px solid #757575 !important;
  border-radius: 0 !important;
  background: transparent !important;
}

.deadline-time-picker {
  width: 100%;
}

.deadline-day-card {
  min-height: 96px;
}

.deadline-day-closed {
  background: #fff8f8;
  border-color: #ef9a9a !important;
}

.closed-day-state {
  display: flex;
  align-items: center;
  min-height: 32px;
}

.closed-day-note {
  font-size: 11px;
  color: #c62828;
  line-height: 1.2;
}

.day-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 22px;
}

.day-switch {
  display: flex;
  align-items: center;
}

.day-switch-label {
  font-size: 11px;
  font-weight: 600;
  margin-right: 2px;
}

.day-switch .v-input--switch {
  flex: 0 0 auto;
}

.day-switch >>> .v-input--selection-controls__input {
  margin-right: 0 !important;
}

.day-card-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.unsaved-warning {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #f57c00;
  padding: 8px 12px;
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: 4px;
}

.search-toggle {
  height: 32px !important;
  border-radius: 6px;
}

.search-toggle .v-btn {
  font-size: 13px;
  padding: 6px 12px !important;
  min-width: 70px;
}

.search-toggle.v-btn-toggle .v-btn.v-btn--active {
  background-color: rgb(92, 184, 96) !important;
  color: white !important;
}

.selected-grades-list {
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  padding: 4px;
}

.selected-grade-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  margin-bottom: 4px;
  transition: background-color 0.2s ease;
}

.selected-grade-item:last-child {
  margin-bottom: 0;
}

.selected-grade-item:hover {
  background-color: #e8f5e9;
}

.selected-grade-item .grade-text {
  font-size: 14px;
  line-height: 1.4;
}
</style>
